// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

// interfaces/types
import type { ReduxMiddleware, ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

// action types
import { PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import { UPDATED_OFFER_PRICE_IMMEDIATE, UPDATED_DOWN_PAYMENT_IMMEDIATE, SELECTED_TERM } from '../actionTypes/offerActionTypes';
import { SWITCH_VEHICLE } from '../actionTypes/openDealActionTypes';
import { INCLUDE_TRADE_INS_CHANGED } from '../actionTypes/tradesActionTypes';
import { TRADE_IN_PAYMENT_INFO_UPDATE } from '../actionTypes/tradeActionTypes';
import { PRODUCT_CHANGES_PAYMENT_CALL } from '../actionTypes/vehicleProtectionActionTypes';
import {
    ADDED_MANUAL_ACCESSORIES,
    ADD_ACCESSORIES_SELECTIONS,
    ADD_CATALOG_ACCESSORIES,
    REMOVE_CATALOG_ACCESSORIES
} from '../actionTypes/accessoriesActionTypes';

// action creators
import { markAltDealStructuresStale } from '../actions/altDealStructureActionCreators';

const relevantActionTypes = new Set([
    UPDATED_OFFER_PRICE_IMMEDIATE,
    UPDATED_DOWN_PAYMENT_IMMEDIATE,
    SELECTED_TERM,
    SWITCH_VEHICLE,
    TRADE_IN_PAYMENT_INFO_UPDATE,
    INCLUDE_TRADE_INS_CHANGED,
    PRODUCT_CHANGES_PAYMENT_CALL,
    ADDED_MANUAL_ACCESSORIES,
    ADD_ACCESSORIES_SELECTIONS,
    ADD_CATALOG_ACCESSORIES,
    REMOVE_CATALOG_ACCESSORIES
]);

/**
 * Alt Deal Structure Staleness Middleware
 *
 * Marks alt deal structure data stale after a payment-recalculated deal parameter change.
 * Only fires on PAYMENT_RECEIVED (not on the original action) so the recalculation is settled
 * before the stale flag is set. Does NOT react to OFFER_SAVE_SUCCESS.
 */
export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);

    if (!action || !action.type) {
        return;
    }

    if (action.type !== PAYMENT_RECEIVED) {
        return;
    }

    const state = store.getState();
    const isEnabled = featureToggleSelectors.isAiAltDealStructureRecommendationsEnabled(state);

    if (!isEnabled) {
        return;
    }

    const originalActionType = action.meta?.originalAction?.type;

    if (relevantActionTypes.has(originalActionType)) {
        store.dispatch(markAltDealStructuresStale());
    }
};
